import {
  Button,
  ButtonWithPopOut,
  LockHollowSVG,
  TooltipDialog,
} from "@newsela/angelou";
import PropTypes from "prop-types";

import { withTrackEvent } from "@newsela/snitch";
import withHoverStateLabel from "@/view/withHoverStateLabel";
import {
  disabledButtonWithPopOutCss,
  disabledSecondaryActionsCss,
  unavailableButtonCss,
  popOutButtonContentsCss,
  popoutButtonWrapperCss,
  popoutButtonCss,
  secondaryActionButtonCss,
  tooltipCss,
} from "./SecondaryActionButton.style";

const ButtonWithPopOutWithTrackEvent = withTrackEvent(
  withHoverStateLabel(ButtonWithPopOut),
);
const ButtonWithTrackEvent = withTrackEvent(withHoverStateLabel(Button));
const TooltipDialogWithTrackEvent = withTrackEvent(
  withHoverStateLabel(TooltipDialog),
);

const propTypes = {
  dataProps: PropTypes.object,
  // Icon that will appear above or next to the action name
  // Add custom icon styles to SecondaryActionButton.style.js
  horizontalPopOutPosition: PropTypes.string,
  icon: PropTypes.node.isRequired,
  isButtonActionUnavailable: PropTypes.bool,
  // Title of action
  title: PropTypes.string.isRequired,
  // Handler for action click
  // Required if not using a popout
  onClick: PropTypes.func,
  eventProperties: PropTypes.object.isRequired,
  // Determines whether or not to show "selected" styles
  isActive: PropTypes.bool,
  // Contents for popout
  // If passed, button with popout will be used
  popOutContents: PropTypes.node,
  ariaProps: PropTypes.object,
  buttonRef: PropTypes.object,
  disabled: PropTypes.bool,
  showLockIcon: PropTypes.bool,
  hoverLabel: PropTypes.string,
};

const SecondaryActionButton = ({
  ariaProps,
  dataProps = {},
  disabled,
  eventProperties,
  horizontalPopOutPosition,
  hoverLabel,
  icon,
  isActive = false,
  isButtonActionUnavailable = false,
  onClick,
  popOutContents,
  showLockIcon,
  buttonRef = {},
  title,
  popupOverrides = {},
}) => {
  const buttonFlavor = Button.flavors.incognito;
  const buttonCss = {
    root: disabled
      ? disabledSecondaryActionsCss
      : [secondaryActionButtonCss(isActive)],
  };

  const buttonWithPopOutCss = {
    root: popoutButtonWrapperCss,
    Button: {
      root: disabled ? disabledButtonWithPopOutCss : popoutButtonCss,
    },
    PopOut: popupOverrides,
  };

  if (popOutContents) {
    const buttonContents = (
      <span css={popOutButtonContentsCss}>
        {icon}
        <span>
          {showLockIcon && (
            <LockHollowSVG isDecorative={false} title="locked" />
          )}
          <span>{title}</span>
        </span>
      </span>
    );

    return (
      <ButtonWithPopOutWithTrackEvent
        ariaProps={ariaProps}
        dataProps={dataProps}
        disabled={disabled}
        id={`secondary-action-${title}`}
        flavor={buttonFlavor}
        __cssFor={buttonWithPopOutCss}
        horizontalPopOutPosition={horizontalPopOutPosition || "RIGHT"}
        verticalOffset="10px"
        horizontalOffset="-175px"
        buttonContents={buttonContents}
        // This is hiding the caret icon
        buttonIcon={<></>}
        popOutContents={popOutContents}
        hoverLabel={hoverLabel}
        ref={buttonRef}
        {...eventProperties}
      />
    );
  }

  if (isButtonActionUnavailable) {
    return (
      <TooltipDialogWithTrackEvent
        __cssFor={tooltipCss}
        ariaProps={ariaProps}
        dataProps={dataProps}
        body={<p>{hoverLabel}</p>}
        buttonContents={
          <div css={unavailableButtonCss}>
            {icon}
            <span>{title}</span>
          </div>
        }
        buttonProps={{
          flavor: buttonFlavor,
        }}
        hoverLabel={hoverLabel}
        placement="top"
        {...eventProperties}
      />
    );
  }

  return (
    <ButtonWithTrackEvent
      __cssFor={buttonCss}
      ariaProps={ariaProps}
      dataProps={dataProps}
      disabled={disabled}
      flavor={buttonFlavor}
      onClick={onClick}
      hoverLabel={hoverLabel}
      ref={buttonRef}
      {...eventProperties}
    >
      {icon}
      <span>{title}</span>
    </ButtonWithTrackEvent>
  );
};

SecondaryActionButton.propTypes = propTypes;

export default SecondaryActionButton;
